import { useState, useEffect, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Topbar } from "@/components/layout/Topbar";
import { ContextualSidebar } from "@/components/layout/ContextualSidebar";
import { NavigationProvider, useNavigation } from "@/contexts/NavigationContext";
import { resolveSidebar, resolveHomeSidebar, workspaceFromPath, WORKSPACES } from "@/lib/navRegistry";
import { AtmosphereLayer } from "@/components/appearance/AtmosphereLayer";
import { SetPasswordPrompt } from "@/components/security/SetPasswordPrompt";
import { WelcomeOnboarding } from "@/components/onboarding/WelcomeOnboarding";
import { TimerProvider } from "@/contexts/TimerContext";
import { surfaceFromPath } from "@/lib/appearance";
import { apiFetch } from "@/lib/api";

function useSidebarMode() {
  const [userMode, setUserMode] = useState(() => {
    try { return localStorage.getItem("nirvana_sidebar_mode") || null; } catch { return null; }
  });
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1440);

  useEffect(() => {
    const on = () => setWidth(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  useEffect(() => {
    apiFetch("/preferences")
      .then((d) => {
        const m = d?.sidebar?.mode;
        if (m) { setUserMode(m); try { localStorage.setItem("nirvana_sidebar_mode", m); } catch {} }
      })
      .catch(() => {});
  }, []);

  // Auto default: laptop widths start compact; large desktop expanded.
  const autoMode = width < 1280 ? "compact" : "expanded";
  const mode = userMode || autoMode;

  const setMode = useCallback((m) => {
    setUserMode(m);
    try { localStorage.setItem("nirvana_sidebar_mode", m); } catch {}
    apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: { sidebar: { mode: m } } }) }).catch(() => {});
  }, []);

  return { mode, setMode, width };
}

function Shell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { config, ctx } = useNavigation();
  const { mode, setMode } = useSidebarMode();
  const wsId = workspaceFromPath(pathname);

  const items = wsId === "settings" ? []
    : wsId === "home" ? resolveHomeSidebar(config, ctx)
    : resolveSidebar(wsId, config, ctx);
  const title = wsId === "home" ? "Home" : (WORKSPACES.find((w) => w.id === wsId)?.label || "Nirvana");

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-background">
      <SetPasswordPrompt />
      <WelcomeOnboarding />
      <Topbar wsId={wsId} onMenuClick={() => setMobileOpen(true)} />
      <div className="flex min-h-0 flex-1">
        <ContextualSidebar title={title} items={items} mode={mode} onModeChange={setMode}
          mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
        <main className="scrollbar-thin relative min-w-0 flex-1 overflow-y-auto">
          <AtmosphereLayer surface={surfaceFromPath(pathname)} />
          <div className="relative z-10 mx-auto w-full" style={{ maxWidth: "var(--content-max, 1280px)", padding: "var(--page-py, 32px) var(--page-px, 32px)" }}><Outlet /></div>
        </main>
      </div>
    </div>
  );
}

export default function AppLayout() {
  return (
    <NavigationProvider><TimerProvider><Shell /></TimerProvider></NavigationProvider>
  );
}
